import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, ArrowRight, CheckCircle2, X } from 'lucide-react';
import { adminApi } from '../../api/adminApi';
import Modal from '../../components/Modal';
import { AdminLayout } from '../../components/layouts';
import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  Input,
  TabButton,
  Tabs,
  Toast,
} from '../../components/ui';

const STATUS_META = {
  pending: { label: 'Pending', variant: 'warning' },
  accepted: { label: 'Accepted', variant: 'success' },
  in_progress: { label: 'In progress', variant: 'info' },
  completed: { label: 'Completed', variant: 'success' },
  cancelled: { label: 'Cancelled', variant: 'danger' },
  auto_cancelled: { label: 'Auto-cancelled', variant: 'danger' },
  declined: { label: 'Declined', variant: 'neutral' },
};

const FILTERS = [
  { key: 'flagged', label: 'Needs attention' },
  { key: 'all', label: 'All' },
  { key: 'pending', label: 'Pending' },
  { key: 'accepted', label: 'Accepted' },
  { key: 'in_progress', label: 'In progress' },
  { key: 'completed', label: 'Completed' },
  { key: 'cancelled', label: 'Cancelled' },
];

const fmtDate = (date) =>
  date ? new Date(date).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';
const fmtDateTime = (date) =>
  date ? new Date(date).toLocaleString('en-AU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';

const Row = ({ label, value, bold }) => (
  <div className="flex flex-col gap-1 py-1.5 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
    <span className="text-xs font-semibold text-giggs-muted">{label}</span>
    <span className={`break-words text-left sm:max-w-[66%] sm:text-right ${bold ? 'text-base font-black text-giggs-green' : 'text-[13px] font-semibold text-giggs-ink'}`}>{value}</span>
  </div>
);

const AdminBookings = () => {
  const [filter, setFilter] = useState('flagged');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ byStatus: {}, flagged: 0 });
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (filter === 'flagged') params.flagged = '1';
      else if (filter !== 'all') params.status = filter;
      if (search.trim()) params.search = search.trim();

      const response = await adminApi.listBookings(params);
      setRows(response.data || []);
      setCounts(response.counts || { byStatus: {}, flagged: 0 });
      setPagination(response.pagination || { page: 1, totalPages: 1, total: 0 });
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to load bookings.');
    } finally {
      setLoading(false);
    }
  }, [filter, search, page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [filter, search]);

  const flash = (message) => {
    setToast(message);
    setTimeout(() => setToast(''), 2500);
  };

  const openDetail = async (id) => {
    setDetailLoading(true);
    try {
      const response = await adminApi.getBookingDetail(id);
      setDetail(response.data);
    } catch (err) {
      flash(err.response?.data?.message || "Couldn't load booking.");
    } finally {
      setDetailLoading(false);
    }
  };

  const doAction = async (key, fn, message) => {
    setBusy(key);
    try {
      await fn();
      flash(message);
      await load();
      if (detail?._id) await openDetail(detail._id);
    } catch (err) {
      flash(err.response?.data?.message || 'Action failed.');
    } finally {
      setBusy('');
    }
  };

  const countFor = (key) =>
    key === 'flagged' ? counts.flagged || 0
      : key === 'all' ? pagination.total
        : counts.byStatus?.[key] || 0;

  return (
    <AdminLayout title="Bookings">
      <div className="mb-6">
        <h2 className="m-0 font-nunito text-[24px] font-black tracking-[-0.025em] text-giggs-ink sm:text-[28px]">Bookings</h2>
        <p className="mb-0 mt-1 text-sm leading-6 text-giggs-muted">Monitor booking activity, resolve exceptions and review job details.</p>
      </div>

      {toast && <Toast><CheckCircle2 size={16} /> {toast}</Toast>}
      {error && <Alert variant="danger" className="mb-4">{error}</Alert>}

      <div className="mb-5 flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
        <Tabs className="w-fit max-w-full overflow-x-auto">
          {FILTERS.map((item) => {
            const count = countFor(item.key);
            const flagged = item.key === 'flagged' && count > 0;
            return (
              <TabButton
                key={item.key}
                active={filter === item.key}
                count={count}
                className={flagged && filter !== item.key ? 'text-amber-700' : ''}
                onClick={() => setFilter(item.key)}
              >
                {item.label}
              </TabButton>
            );
          })}
        </Tabs>

        <Input
          className="xl:max-w-[260px]"
          placeholder="Search tracking ID…"
          value={search}
          onChange={(event) => setSearch(event.target.value.toUpperCase())}
        />
      </div>

      <Card className="overflow-hidden p-2 sm:p-2.5">
        {loading ? (
          <EmptyState title="Loading bookings…" description="Fetching the latest booking activity." />
        ) : rows.length === 0 ? (
          <EmptyState title="No bookings found" description="No bookings match the current filter." />
        ) : (
          <div className="flex flex-col gap-1">
            {rows.map((booking) => {
              const meta = STATUS_META[booking.status] || STATUS_META.pending;
              return (
                <button
                  type="button"
                  key={booking._id}
                  onClick={() => openDetail(booking._id)}
                  className="group grid w-full cursor-pointer grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-2 rounded-xl border-0 bg-white px-4 py-3.5 text-left transition-all duration-200 hover:bg-[#F8FAF9] sm:grid-cols-[minmax(0,1fr)_auto_80px] sm:gap-4"
                >
                  <span className="min-w-0">
                    <span className="flex items-center gap-2">
                      <span className="truncate text-sm font-bold text-giggs-ink">{booking.service?.name}</span>
                      {booking.needsAttention && (
                        <span title="Needs attention" className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-amber-50 text-amber-600">
                          <AlertTriangle size={13} />
                        </span>
                      )}
                    </span>
                    <span className="mt-1 block break-words text-xs leading-5 text-giggs-muted">{booking.trackingId} · {booking.clientName} → {booking.gigger?.name} · {fmtDate(booking.scheduledDate)}</span>
                  </span>
                  <Badge variant={meta.variant}>{meta.label}</Badge>
                  <span className="col-start-2 flex items-center justify-end gap-2 text-right text-sm font-black text-giggs-ink sm:col-start-auto">
                    ${booking.total}
                    <ArrowRight size={14} className="text-giggs-muted transition-transform duration-200 group-hover:translate-x-0.5 group-hover:text-giggs-green" />
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </Card>

      {pagination.totalPages > 1 && (
        <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((current) => current - 1)}>Prev</Button>
          <span className="text-center text-xs font-semibold text-giggs-muted">Page {pagination.page} of {pagination.totalPages}</span>
          <Button variant="outline" size="sm" disabled={page >= pagination.totalPages} onClick={() => setPage((current) => current + 1)}>Next</Button>
        </div>
      )}

      {(detail || detailLoading) && (
        <Modal title={detail?.service?.name || 'Booking details'} size="md" onClose={() => setDetail(null)}>
          <div className="px-5 pb-6 pt-2 sm:px-8 sm:pb-8">
            {detailLoading || !detail ? (
              <EmptyState title="Loading booking…" />
            ) : (
              <BookingDetail booking={detail} busy={busy} doAction={doAction} onClose={() => setDetail(null)} />
            )}
          </div>
        </Modal>
      )}
    </AdminLayout>
  );
};

const BookingDetail = ({ booking, busy, doAction, onClose }) => {
  const meta = STATUS_META[booking.status] || STATUS_META.pending;
  const location = booking.location || {};
  const needsCancelReview = booking.cancellation?.adminReviewStatus === 'pending';
  const escalated = booking.adminEndRequest?.requested && booking.status === 'in_progress';

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="break-all text-xs font-bold text-giggs-muted">{booking.trackingId}</div>
          <div className="mt-2"><Badge variant={meta.variant}>{meta.label}</Badge></div>
        </div>
        <Button variant="ghost" size="icon" className="!h-9 !w-9" onClick={onClose} aria-label="Close booking details"><X size={17} /></Button>
      </div>

      {escalated && (
        <div className="rounded-2xl bg-amber-50 p-4 ring-1 ring-inset ring-amber-100">
          <div className="mb-1 flex items-center gap-2 font-bold text-amber-800"><AlertTriangle size={16} /> Gigger asked you to end this job</div>
          <div className="mb-3 break-words text-[13px] leading-5 text-amber-800/80">{booking.adminEndRequest.reason}</div>
          <Button className="w-full" disabled={busy === 'end'} onClick={() => doAction('end', () => adminApi.endBooking(booking._id), 'Job ended.')}>End job now</Button>
        </div>
      )}

      {needsCancelReview && (
        <div className="rounded-2xl bg-amber-50 p-4 ring-1 ring-inset ring-amber-100">
          <div className="mb-1 font-bold text-amber-800">Cancellation needs a decision</div>
          <div className="mb-3 break-words text-[13px] leading-5 text-amber-800/80">{booking.cancellation.detail || 'No detail provided.'}</div>
          <div className="flex flex-col gap-2.5 sm:flex-row">
            <Button variant="danger" className="flex-1" disabled={busy === 'pen'} onClick={() => doAction('pen', () => adminApi.resolveCancellation(booking._id, 'penalise'), 'Strike applied.')}>Apply strike</Button>
            <Button variant="outline" className="flex-1" disabled={busy === 'dis'} onClick={() => doAction('dis', () => adminApi.resolveCancellation(booking._id, 'dismiss'), 'Dismissed.')}>Dismiss</Button>
          </div>
        </div>
      )}

      <div className="rounded-2xl bg-[#F8FAF9] p-4 sm:p-5">
        <Row label="Client" value={`${booking.client?.name} · ${booking.client?.phone || '—'}`} />
        <Row label="Gigger" value={`${booking.gigger?.name} · ${booking.gigger?.negativeMarks || 0} strikes`} />
        <Row label="Date" value={`${fmtDate(booking.scheduledDate)}, ${booking.startTime}–${booking.endTime}`} />
        <Row label="Location" value={[location.address, location.suburb, location.postcode].filter(Boolean).join(', ') || '—'} />
        <div className="my-2 h-px bg-slate-200/70" />
        <Row label="Gigger payout" value={`$${booking.subtotal}`} />
        <Row label="Platform fee" value={`$${booking.platformFee}`} />
        <Row label="Client total" value={`$${booking.total}`} bold />
      </div>

      <div className="rounded-2xl bg-[#F8FAF9] p-4 sm:p-5">
        <div className="mb-2 font-nunito text-sm font-extrabold text-giggs-ink">Timeline</div>
        <Row label="Submitted" value={fmtDateTime(booking.submittedAt)} />
        <Row label="Responded" value={fmtDateTime(booking.respondedAt)} />
        <Row label="Start req / confirmed" value={`${fmtDateTime(booking.startRequestedAt)} / ${fmtDateTime(booking.startConfirmedAt)}`} />
        <Row label="End req / confirmed" value={`${fmtDateTime(booking.endRequestedAt)} / ${fmtDateTime(booking.endConfirmedAt)}`} />
        {booking.endedBy && <Row label="Ended by" value={booking.endedBy} />}
      </div>

      {booking.review?.rating && (
        <div className="rounded-2xl bg-[#F8FAF9] p-4 sm:p-5">
          <div className="mb-1 font-bold text-giggs-ink">Review · {booking.review.rating}★</div>
          <div className="break-words text-[13px] leading-5 text-giggs-text">{booking.review.comment || 'No comment.'}</div>
        </div>
      )}

      {booking.status === 'in_progress' && !escalated && (
        <Button variant="outline" className="w-full" disabled={busy === 'end'} onClick={() => doAction('end', () => adminApi.endBooking(booking._id), 'Job ended.')}>Force-end this job</Button>
      )}
    </div>
  );
};

export default AdminBookings;
